import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  CheckIcon,
  TrashIcon,
  CurrencyDollarIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

/**
 * ChatToolResult - Renders tool results from the Klo chat assistant
 * Each tool type has its own specialized UI card
 *
 * @param {boolean} isLatest - Only the latest tool result should have keyboard shortcuts
 */
const ChatToolResult = ({ result, onAction, isLatest = false }) => {
  const { type, data } = result;

  switch (type) {
    case 'client_search':
      return <ClientSearchCard data={data} onAction={onAction} isLatest={isLatest} />;
    case 'client_created':
      return <ClientCreatedCard data={data} />;
    case 'clients_list':
      return <ClientsListCard data={data} onAction={onAction} isLatest={isLatest} />;
    case 'services_list':
      return <ServicesListCard data={data} onAction={onAction} />;
    case 'invoice_created':
      return <InvoiceCreatedCard data={data} onAction={onAction} isLatest={isLatest} />;
    case 'invoices_list':
      return <InvoicesListCard data={data} onAction={onAction} isLatest={isLatest} />;
    case 'delete_invoice':
      return <DeleteConfirmCard data={data} onAction={onAction} isLatest={isLatest} type="invoice" />;
    case 'delete_client':
      return <DeleteConfirmCard data={data} onAction={onAction} isLatest={isLatest} type="client" />;
    case 'invoice_status_updated':
      return <InvoiceStatusCard data={data} />;
    case 'email_preview':
      return <EmailPreviewCard data={data} onAction={onAction} />;
    case 'email_sent':
      return <EmailSentCard data={data} onAction={onAction} />;
    case 'bulk_delete_invoices':
      return <BulkActionConfirmCard data={data} onAction={onAction} isLatest={isLatest} actionType="delete" itemType="invoice" />;
    case 'bulk_delete_clients':
      return <BulkActionConfirmCard data={data} onAction={onAction} isLatest={isLatest} actionType="delete" itemType="client" />;
    case 'bulk_mark_paid':
      return <BulkActionConfirmCard data={data} onAction={onAction} isLatest={isLatest} actionType="mark_paid" itemType="invoice" />;
    case 'bulk_send_invoices':
      return <BulkActionConfirmCard data={data} onAction={onAction} isLatest={isLatest} actionType="send" itemType="invoice" />;
    case 'error':
      return <ErrorCard data={data} />;
    default:
      return null;
  }
};

/**
 * Client Search Results Card with Confirmation
 * Single client = pre-selected, user confirms with button or ⌘+Enter
 * No results = shows new client form
 */
const ClientSearchCard = ({ data, onAction, isLatest }) => {
  const { clients, query } = data;
  // Auto-select if only one client (pre-highlighted, user still confirms)
  const [selectedClient, setSelectedClient] = useState(
    clients?.length === 1 ? clients[0] : null
  );

  // New client form state
  const [newClientName, setNewClientName] = useState(query || '');
  const [newClientEmail, setNewClientEmail] = useState('');
  const emailInputRef = React.useRef(null);

  // Focus email input when showing new client form
  useEffect(() => {
    if ((!clients || clients.length === 0) && isLatest && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [clients, isLatest]);

  // Handle keyboard shortcuts - only when this is the latest card
  const handleKeyDown = useCallback((e) => {
    if (!isLatest) return;

    // For existing client selection
    if (selectedClient) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        onAction('select_client', { client: selectedClient });
        setSelectedClient(null);
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setSelectedClient(null);
      }
    }

    // For new client form - ⌘+Enter to submit
    if (!clients || clients.length === 0) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        if (newClientName.trim() && newClientEmail.trim()) {
          onAction('create_client', { name: newClientName.trim(), email: newClientEmail.trim() });
        }
      }
    }
  }, [selectedClient, onAction, isLatest, clients, newClientName, newClientEmail]);

  useEffect(() => {
    if (isLatest && (selectedClient || (!clients || clients.length === 0))) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [selectedClient, handleKeyDown, isLatest, clients]);

  // No clients found - show new client form
  if (!clients || clients.length === 0) {
    const canSubmit = newClientName.trim() && newClientEmail.trim();

    return (
      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-[#1A1A1A] rounded-xl border border-gray-200 dark:border-[#333] overflow-hidden shadow-sm"
      >
        <div className="px-4 py-2 bg-gray-50 dark:bg-[#0D0D0D] border-b border-gray-100 dark:border-[#333]">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">New Client</span>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
            <input
              type="text"
              value={newClientName}
              onChange={(e) => setNewClientName(e.target.value)}
              placeholder="Client name"
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-[#333] rounded-lg focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand bg-white dark:bg-[#0D0D0D] text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
            <input
              ref={emailInputRef}
              type="email"
              value={newClientEmail}
              onChange={(e) => setNewClientEmail(e.target.value)}
              placeholder="client@email.com"
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-[#333] rounded-lg focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand bg-white dark:bg-[#0D0D0D] text-gray-900 dark:text-white"
            />
          </div>
          <div className="flex items-center justify-end gap-3 pt-1">
            <span className="text-xs text-gray-400 dark:text-gray-500 dark:text-gray-400">
              {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter
            </span>
            <button
              onClick={() => {
                if (canSubmit) {
                  onAction('create_client', { name: newClientName.trim(), email: newClientEmail.trim() });
                }
              }}
              disabled={!canSubmit}
              className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
                canSubmit
                  ? 'bg-brand text-white hover:bg-brand-hover'
                  : 'bg-gray-100 dark:bg-[#2A2A2A] text-gray-400 dark:text-gray-600 cursor-not-allowed'
              }`}
            >
              Continue
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-2"
    >
      {/* Client list */}
      <div className="bg-white dark:bg-[#1A1A1A] rounded-xl border border-gray-200 dark:border-[#333] overflow-hidden shadow-sm">
        <div className="divide-y divide-gray-100 dark:divide-[#333]">
          {clients.map((client) => (
            <button
              key={client.id}
              onClick={() => setSelectedClient(selectedClient?.id === client.id ? null : client)}
              className={`w-full px-4 py-3 flex items-center gap-3 transition-all text-left ${
                selectedClient?.id === client.id
                  ? 'bg-brand text-white'
                  : 'hover:bg-gray-50 dark:hover:bg-white/5'
              }`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                selectedClient?.id === client.id ? 'bg-white/20' : 'bg-brand/10'
              }`}>
                <UserIcon className={`w-5 h-5 ${selectedClient?.id === client.id ? 'text-white' : 'text-brand'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${selectedClient?.id === client.id ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                  {client.name}
                </p>
                <p className={`text-xs truncate ${selectedClient?.id === client.id ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>
                  {client.email}
                </p>
              </div>
              {selectedClient?.id === client.id && (
                <CheckIcon className="w-5 h-5 text-white flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Floating confirm bar - only for multiple clients */}
      <AnimatePresence>
        {selectedClient && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="flex items-center justify-end gap-3"
          >
            <span className="text-xs text-gray-400 dark:text-gray-500 dark:text-gray-400">
              {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter
            </span>
            <button
              onClick={() => {
                onAction('select_client', { client: selectedClient });
                setSelectedClient(null);
              }}
              className="px-4 py-2 bg-brand text-white rounded-xl hover:bg-brand-hover transition-colors text-sm font-medium shadow-sm"
            >
              Use {selectedClient.name.split(' ')[0]}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/**
 * Client Created Card - Shows when a new client is added
 */
const ClientCreatedCard = ({ data }) => {
  const { success, client, message, error, existing } = data;

  if (!success) {
    return <ErrorCard data={{ error: error || 'Failed to add client' }} />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl border overflow-hidden shadow-sm ${
        existing ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'
      }`}
    >
      <div className="p-3 flex items-center gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          existing ? 'bg-blue-100' : 'bg-green-100'
        }`}>
          {existing ? (
            <UserIcon className={`w-4 h-4 text-blue-600`} />
          ) : (
            <CheckCircleIcon className={`w-4 h-4 text-green-600`} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${existing ? 'text-blue-800' : 'text-green-800'}`}>
            {message || (existing ? `${client?.name} already exists` : `Added ${client?.name}`)}
          </p>
          <p className={`text-xs ${existing ? 'text-blue-600' : 'text-green-600'}`}>
            {client?.email}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

/**
 * Services List Card
 */
const ServicesListCard = ({ data, onAction }) => {
  const { services, hasServices } = data;

  if (!hasServices) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-50 dark:bg-[#1A1A1A] rounded-xl p-4 border border-gray-200 dark:border-[#333]"
      >
        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
          <DocumentTextIcon className="w-5 h-5" />
          <span className="text-sm">No saved services found</span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-[#1A1A1A] rounded-xl border border-gray-200 dark:border-[#333] overflow-hidden shadow-sm"
    >
      <div className="px-4 py-2 bg-gray-50 dark:bg-[#0D0D0D] border-b border-gray-100 dark:border-[#333]">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Your Services</span>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-[#333]">
        {services.map((service) => (
          <div
            key={service.id}
            className="px-4 py-3 flex items-center justify-between"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white">{service.name}</p>
              {service.description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{service.description}</p>
              )}
            </div>
            {service.price && (
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                ${service.price.toLocaleString()}
              </span>
            )}
          </div>
        ))}
      </div>
    </motion.div>
  );
};

/**
 * Invoice Created Card - With integrated Send button and ⌘+Enter shortcut
 */
const InvoiceCreatedCard = ({ data, onAction, isLatest }) => {
  const navigate = useNavigate();
  const { success, invoice, previewUrl, error, invoiceId } = data;

  // Handle ⌘+Enter to send - only when this is the latest card
  const handleKeyDown = useCallback((e) => {
    if (!isLatest) return;
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      onAction('send_email', { invoiceId: invoiceId || invoice?.id });
    }
  }, [onAction, invoiceId, invoice?.id, isLatest]);

  useEffect(() => {
    // Only add keyboard listener if this is the latest card
    if (isLatest) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [handleKeyDown, isLatest]);

  if (!success) {
    return <ErrorCard data={{ error: error || 'Failed to create invoice' }} />;
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: invoice?.currency || 'USD'
    }).format(amount);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-[#1A1A1A] rounded-xl border border-gray-200 dark:border-[#333] overflow-hidden shadow-sm"
    >
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-500/10 flex items-center justify-center">
              <CheckCircleIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">{invoice?.clientName}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{invoice?.email}</p>
            </div>
          </div>
          <span className="text-lg font-semibold text-gray-900 dark:text-white">{formatCurrency(invoice?.total)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400 dark:text-gray-500">Due {invoice?.dueDate}</span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter
            </span>
            <button
              onClick={() => navigate(previewUrl)}
              className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              View
            </button>
            <button
              onClick={() => onAction('send_email', { invoiceId: invoiceId || invoice?.id })}
              className="px-3 py-1.5 text-xs font-medium text-white bg-brand rounded-lg hover:bg-brand-hover transition-colors"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/**
 * Email Preview Card - Now just a fallback, rarely shown
 */
const EmailPreviewCard = ({ data, onAction }) => {
  const { to, error, invoiceId } = data;

  if (error) {
    return <ErrorCard data={{ error }} />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center justify-between py-2"
    >
      <span className="text-xs text-gray-500 dark:text-gray-400">
        Ready to send to <span className="text-gray-700">{to}</span>
      </span>
      <button
        onClick={() => onAction('send_email', { invoiceId })}
        className="px-3 py-1.5 text-xs font-medium text-white bg-brand rounded-lg hover:bg-brand-hover transition-colors"
      >
        Send
      </button>
    </motion.div>
  );
};

/**
 * Email Sent Confirmation Card - Compact version
 */
const EmailSentCard = ({ data }) => {
  const navigate = useNavigate();
  const { success, sentTo, previewUrl, error } = data;

  if (!success) {
    return <ErrorCard data={{ error: error || 'Failed to send email' }} />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-green-50 rounded-xl border border-green-200 overflow-hidden shadow-sm"
    >
      <div className="p-3 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-800">
            Sent to <span className="font-medium">{sentTo}</span>
          </p>
        </div>
        {previewUrl && (
          <button
            onClick={() => navigate(previewUrl)}
            className="px-2.5 py-1.5 text-xs font-medium text-brand border border-brand rounded-lg hover:bg-brand/5 transition-colors flex-shrink-0"
          >
            View
          </button>
        )}
      </div>
    </motion.div>
  );
};

/**
 * Invoices List Card - Shows list of invoices
 */
const InvoicesListCard = ({ data, onAction, isLatest }) => {
  const navigate = useNavigate();
  const { invoices, query, status, totalFound, error } = data;

  if (error) {
    return <ErrorCard data={{ error }} />;
  }

  if (!invoices || invoices.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-50 dark:bg-[#1A1A1A] rounded-xl p-4 border border-gray-200 dark:border-[#333]"
      >
        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
          <DocumentTextIcon className="w-5 h-5" />
          <span className="text-sm">
            {query ? `No invoices found for "${query}"` : 'No invoices found'}
          </span>
        </div>
      </motion.div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'bg-green-100 text-green-700';
      case 'PENDING': return 'bg-gray-100 text-gray-600';
      case 'OVERDUE': return 'bg-red-100 text-red-700';
      case 'CANCELLED': return 'bg-gray-100 text-gray-500';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-[#1A1A1A] rounded-xl border border-gray-200 dark:border-[#333] overflow-hidden shadow-sm"
    >
      <div className="px-4 py-2 bg-gray-50 dark:bg-[#0D0D0D] border-b border-gray-100 dark:border-[#333] flex items-center justify-between">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {query ? `Invoices for "${query}"` : status ? `${status} Invoices` : 'Recent Invoices'}
        </span>
        <span className="text-xs text-gray-400 dark:text-gray-500">{totalFound} found</span>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-[#333] max-h-64 overflow-y-auto">
        {invoices.map((invoice) => (
          <button
            key={invoice.id}
            onClick={() => navigate(`/preview-invoice/${invoice.id}`)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-left"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0">
                <CurrencyDollarIcon className="w-4 h-4 text-brand" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{invoice.clientName}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{invoice.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                {invoice.status}
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{formatCurrency(invoice.total)}</span>
            </div>
          </button>
        ))}
      </div>
    </motion.div>
  );
};

/**
 * Clients List Card - Shows list of clients
 */
const ClientsListCard = ({ data, onAction, isLatest }) => {
  const { clients, query, totalFound, error } = data;

  if (error) {
    return <ErrorCard data={{ error }} />;
  }

  if (!clients || clients.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-50 dark:bg-[#1A1A1A] rounded-xl p-4 border border-gray-200 dark:border-[#333]"
      >
        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
          <UserIcon className="w-5 h-5" />
          <span className="text-sm">
            {query ? `No clients found matching "${query}"` : 'No clients yet'}
          </span>
        </div>
      </motion.div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-[#1A1A1A] rounded-xl border border-gray-200 dark:border-[#333] overflow-hidden shadow-sm"
    >
      <div className="px-4 py-2 bg-gray-50 dark:bg-[#0D0D0D] border-b border-gray-100 dark:border-[#333] flex items-center justify-between">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {query ? `Clients matching "${query}"` : 'Your Clients'}
        </span>
        <span className="text-xs text-gray-400 dark:text-gray-500">{totalFound} found</span>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-[#333] max-h-64 overflow-y-auto">
        {clients.map((client) => (
          <div
            key={client.id}
            className="px-4 py-3 flex items-center justify-between"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0">
                <UserIcon className="w-5 h-5 text-brand" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{client.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{client.email}</p>
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(client.totalBilled)}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{client.totalInvoices} invoices</p>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

/**
 * Delete Confirmation Card - For confirming delete actions
 */
const DeleteConfirmCard = ({ data, onAction, isLatest, type }) => {
  const { success, deleted, needsConfirmation, invoice, client, error, clientName } = data;

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    if (!isLatest || !needsConfirmation) return;

    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      const id = type === 'invoice' ? invoice?.id : client?.id;
      onAction(`confirm_delete_${type}`, { id, confirmed: true });
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onAction('cancel_delete');
    }
  }, [isLatest, needsConfirmation, onAction, type, invoice, client]);

  useEffect(() => {
    if (isLatest && needsConfirmation) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isLatest, needsConfirmation, handleKeyDown]);

  if (error) {
    return <ErrorCard data={{ error }} />;
  }

  // Successfully deleted
  if (success && deleted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-50 dark:bg-[#1A1A1A] rounded-xl p-3 border border-gray-200 dark:border-[#333]"
      >
        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
          <CheckCircleIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          <span className="text-sm">
            {type === 'invoice' ? `Deleted invoice for ${clientName}` : `Removed ${clientName}`}
          </span>
        </div>
      </motion.div>
    );
  }

  // Needs confirmation
  if (needsConfirmation) {
    const item = type === 'invoice' ? invoice : client;
    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-red-50 rounded-xl border border-red-200 overflow-hidden shadow-sm"
      >
        <div className="p-4">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <TrashIcon className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-800">
                Delete {type === 'invoice' ? 'Invoice' : 'Client'}?
              </p>
              <p className="text-sm text-red-700 mt-1">{item?.name || item?.clientName}</p>
              <p className="text-xs text-red-600">{item?.email}</p>
              {type === 'invoice' && (
                <p className="text-sm font-medium text-red-800 mt-1">{formatCurrency(item?.total)}</p>
              )}
              {type === 'client' && item?.invoicesCount > 0 && (
                <p className="text-xs text-red-600 mt-1">Has {item.invoicesCount} invoice(s)</p>
              )}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <span className="text-xs text-red-400 mr-2">
              {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter
            </span>
            <button
              onClick={() => onAction('cancel_delete')}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                const id = type === 'invoice' ? item?.id : item?.id;
                onAction(`confirm_delete_${type}`, { id, confirmed: true });
              }}
              className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return null;
};

/**
 * Invoice Status Updated Card
 */
const InvoiceStatusCard = ({ data }) => {
  const { success, clientName, total, oldStatus, newStatus, error } = data;

  if (error || !success) {
    return <ErrorCard data={{ error: error || 'Failed to update status' }} />;
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'bg-green-100 text-green-700';
      case 'PENDING': return 'bg-gray-100 text-gray-600';
      case 'CANCELLED': return 'bg-gray-100 text-gray-500';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-green-50 rounded-xl p-3 border border-green-200"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckCircleIcon className="w-5 h-5 text-green-600" />
          <div>
            <p className="text-sm text-green-800">
              <span className="font-medium">{clientName}</span> - {formatCurrency(total)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium line-through opacity-50 ${getStatusColor(oldStatus)}`}>
            {oldStatus}
          </span>
          <span className="text-gray-400">→</span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(newStatus)}`}>
            {newStatus}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

/**
 * Bulk Action Confirmation Card - For confirming bulk actions on invoices or clients
 */
const BulkActionConfirmCard = ({ data, onAction, isLatest, actionType, itemType = 'invoice' }) => {
  const { success, needsConfirmation, invoices, clients, count, deletedCount, updatedCount, sentCount, error } = data;

  // Use either invoices or clients depending on item type
  const items = itemType === 'client' ? clients : invoices;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'bg-green-100 text-green-700';
      case 'PENDING': return 'bg-gray-100 text-gray-600';
      case 'OVERDUE': return 'bg-red-100 text-red-700';
      case 'CANCELLED': return 'bg-gray-100 text-gray-500';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getActionConfig = () => {
    if (actionType === 'delete' && itemType === 'client') {
      return {
        title: 'Delete Clients',
        confirmText: 'Delete All',
        confirmAction: 'confirm_bulk_delete_clients',
        icon: TrashIcon,
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        iconBg: 'bg-red-100',
        iconColor: 'text-red-600',
        titleColor: 'text-red-800',
        buttonBg: 'bg-red-600 hover:bg-red-700',
        successMessage: `Deleted ${deletedCount} client${deletedCount !== 1 ? 's' : ''}`
      };
    }

    switch (actionType) {
      case 'delete':
        return {
          title: 'Delete Invoices',
          confirmText: 'Delete All',
          confirmAction: 'confirm_bulk_delete_invoices',
          icon: TrashIcon,
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          iconBg: 'bg-red-100',
          iconColor: 'text-red-600',
          titleColor: 'text-red-800',
          buttonBg: 'bg-red-600 hover:bg-red-700',
          successMessage: `Deleted ${deletedCount} invoice${deletedCount !== 1 ? 's' : ''}`
        };
      case 'mark_paid':
        return {
          title: 'Mark as Paid',
          confirmText: 'Mark All Paid',
          confirmAction: 'confirm_bulk_mark_paid',
          icon: CheckCircleIcon,
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          iconBg: 'bg-green-100',
          iconColor: 'text-green-600',
          titleColor: 'text-green-800',
          buttonBg: 'bg-green-600 hover:bg-green-700',
          successMessage: `Marked ${updatedCount} invoice${updatedCount !== 1 ? 's' : ''} as paid`
        };
      case 'send':
        return {
          title: 'Send Invoices',
          confirmText: 'Send All',
          confirmAction: 'confirm_bulk_send_invoices',
          icon: PaperAirplaneIcon,
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600',
          titleColor: 'text-blue-800',
          buttonBg: 'bg-blue-600 hover:bg-blue-700',
          successMessage: `Sent ${sentCount} invoice${sentCount !== 1 ? 's' : ''}`
        };
      default:
        return {};
    }
  };

  const config = getActionConfig();
  const IconComponent = config.icon;

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    if (!isLatest || !needsConfirmation) return;

    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      const ids = items.map(item => item.id);
      if (itemType === 'client') {
        onAction(config.confirmAction, { clientIds: ids });
      } else {
        onAction(config.confirmAction, { invoiceIds: ids });
      }
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onAction('cancel_bulk_action');
    }
  }, [isLatest, needsConfirmation, onAction, config.confirmAction, items, itemType]);

  useEffect(() => {
    if (isLatest && needsConfirmation) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isLatest, needsConfirmation, handleKeyDown]);

  if (error) {
    return <ErrorCard data={{ error }} />;
  }

  // Successfully completed
  if (success && !needsConfirmation) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className={`${config.bgColor} rounded-xl p-3 border ${config.borderColor}`}
      >
        <div className="flex items-center gap-2">
          <CheckCircleIcon className={`w-5 h-5 ${config.iconColor}`} />
          <span className={`text-sm ${config.titleColor}`}>{config.successMessage}</span>
        </div>
      </motion.div>
    );
  }

  // Needs confirmation - show list
  if (needsConfirmation && items && items.length > 0) {
    const totalAmount = itemType === 'invoice' ? items.reduce((sum, item) => sum + (item.total || 0), 0) : 0;

    return (
      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className={`${config.bgColor} rounded-xl border ${config.borderColor} overflow-hidden shadow-sm`}
      >
        <div className={`px-4 py-3 border-b ${config.borderColor} flex items-center gap-3`}>
          <div className={`w-10 h-10 rounded-full ${config.iconBg} flex items-center justify-center`}>
            <IconComponent className={`w-5 h-5 ${config.iconColor}`} />
          </div>
          <div>
            <p className={`text-sm font-semibold ${config.titleColor}`}>{config.title}</p>
            <p className={`text-xs ${config.iconColor}`}>
              {count} {itemType === 'client' ? 'client' : 'invoice'}{count !== 1 ? 's' : ''}
              {itemType === 'invoice' && ` - ${formatCurrency(totalAmount)} total`}
            </p>
          </div>
        </div>

        <div className="max-h-48 overflow-y-auto divide-y divide-gray-100 dark:divide-[#333]">
          {items.map((item, idx) => (
            <div key={item.id} className="px-4 py-2 flex items-center justify-between bg-white/50">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs text-gray-400 w-5">{idx + 1}.</span>
                <div className="min-w-0">
                  <span className="text-sm text-gray-900 truncate block">
                    {itemType === 'client' ? item.name : item.clientName}
                  </span>
                  {itemType === 'client' && (
                    <span className="text-xs text-gray-500 truncate block">{item.email}</span>
                  )}
                </div>
              </div>
              {itemType === 'invoice' && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getStatusColor(item.status)}`}>
                    {item.status}
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(item.total)}</span>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="px-4 py-3 bg-white/50 flex items-center justify-end gap-2 border-t border-gray-100">
          <span className={`text-xs ${config.iconColor} mr-2`}>
            {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter
          </span>
          <button
            onClick={() => onAction('cancel_bulk_action')}
            className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              const ids = items.map(item => item.id);
              if (itemType === 'client') {
                onAction(config.confirmAction, { clientIds: ids });
              } else {
                onAction(config.confirmAction, { invoiceIds: ids });
              }
            }}
            className={`px-3 py-1.5 text-xs font-medium text-white ${config.buttonBg} rounded-lg transition-colors`}
          >
            {config.confirmText}
          </button>
        </div>
      </motion.div>
    );
  }

  return null;
};

/**
 * Error Card
 */
const ErrorCard = ({ data }) => {
  const { error } = data;

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-red-50 dark:bg-red-500/10 rounded-xl p-4 border border-red-200 dark:border-red-500/20"
    >
      <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
        <ExclamationCircleIcon className="w-5 h-5" />
        <span className="text-sm">{error || 'Something went wrong'}</span>
      </div>
    </motion.div>
  );
};

export default ChatToolResult;
